import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../contexts/LanguageContext';
import { useAuth } from '../contexts/AuthContext';
import { motion, useInView, useMotionValue, animate } from 'framer-motion';
import useReducedMotion from '../hooks/useReducedMotion';
import { EASE_OUT } from '../lib/motionVariants';
import Logo from '../components/Logo';
import { ArrowRight } from 'lucide-react';
import HeroSlider from '../components/landing/HeroSlider';
import PopularCourses from '../components/landing/PopularCourses';
import CurriculumGrid from '../components/landing/CurriculumGrid';
import FormatCards from '../components/landing/FormatCards';
import RoadmapSteps from '../components/landing/RoadmapSteps';
import TestimonialsSection from '../components/landing/TestimonialsSection';
import FaqAccordion from '../components/landing/FaqAccordion';
import WhyUsSection from '../components/landing/WhyUsSection';
import { cleanCourseList } from '../lib/landingCourses';
import { localeUpperCase } from '../lib/textCase';
import { buildWhatsAppUrl } from '../config/contact';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Pazarlama tabanı: platforma kayıt öncesi dönemden (yüz yüze eğitim, dış kanal,
// göç edilen eski sistem vb.) gelen ve veritabanında ayrı kaydı bulunmayan öğrenci
// sayısı. Gerçek zamanlı /api/courses toplamının üstüne eklenir; SABİT bir gösterge
// DEĞİLDİR — gerçek kayıtlar arttıkça toplam da artmaya devam eder. Sadece API
// başarıyla veri döndürdüğünde uygulanır (bkz. useLiveCatalogStats catch bloğu).
const STUDENT_COUNT_BASELINE = 5000;

function AnimatedCounter({ value, suffix = '', duration = 1.4 }) {
  const reduced = useReducedMotion();
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.4 });
  const mv = useMotionValue(0);
  const [display, setDisplay] = useState(reduced ? value : 0);

  useEffect(() => {
    if (reduced) { setDisplay(value); return; }
    if (!inView) return;
    const controls = animate(mv, value, {
      duration,
      ease: EASE_OUT,
      onUpdate: (latest) => setDisplay(Math.floor(latest)),
    });
    return () => controls.stop();
  }, [inView, value, duration, mv, reduced]);

  return <span ref={ref}>{display.toLocaleString()}{suffix}</span>;
}

/**
 * Canlı katalog istatistikleri — mockup'taki sabit "50+/5.000+/800+" gibi
 * pazarlama rakamları yerine gerçek /api/courses verisinden hesaplanır.
 * NOT (karar gerekli): DB'de demo/test kurslar temizlenmeden bu rakamlar
 * gerçek üretim kataloğunu tam yansıtmaz; bkz. rapor.
 */
function useLiveCatalogStats() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/courses?limit=100`)
      .then((res) => {
        if (cancelled) return;
        const cleaned = cleanCourseList(res.data?.courses || []);
        const realStudents = cleaned.reduce((sum, c) => sum + (c.enrolled_count || 0), 0);
        setStats({
          courses: cleaned.length,
          lessons: cleaned.reduce((sum, c) => sum + (c.lessons_count || 0), 0),
          // Gerçek kayıtlı öğrenci sayısına pazarlama tabanı eklenir (bkz. STUDENT_COUNT_BASELINE).
          students: STUDENT_COUNT_BASELINE + realStudents,
        });
      })
      .catch(() => {
        // API hata verirse taban da uygulanmaz: gerçek veri doğrulanamadığından
        // 5000 gibi bir rakamı da gösterip yanıltıcı olmaktansa 0/0/0 ile tutarlı kalıyoruz.
        if (!cancelled) setStats({ courses: 0, lessons: 0, students: 0 });
      });
    return () => { cancelled = true; };
  }, []);

  return stats;
}

export default function LandingPage() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  const reduced = useReducedMotion();
  const stats = useLiveCatalogStats();

  useEffect(() => {
    document.body.classList.add('duo-page');
    return () => document.body.classList.remove('duo-page');
  }, []);

  return (
    <div data-testid="landing-page">
      {/* ===== HERO ===== */}
      <HeroSlider />

      <PopularCourses />
      <CurriculumGrid />
      <FormatCards />
      <RoadmapSteps />
      <TestimonialsSection />
      <FaqAccordion />
      <WhyUsSection />

      {/* ===== FINAL CTA ===== */}
      <section className="relative overflow-hidden py-20 px-6" style={{ background: 'linear-gradient(150deg,#122a54,#0B1A33)' }} data-testid="landing2-final-cta">
        <div className="max-w-3xl mx-auto text-center relative">
          <motion.div
            variants={reduced ? undefined : { hidden: { opacity: 0, y: 24 }, visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: EASE_OUT } } }}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.25 }}
          >
            <h2 className="landing2-display text-3xl md:text-5xl text-[#F7F5F0]">
              {localeUpperCase(t('landing2.finalCta.title'), lang)}
            </h2>
            <p className="text-lg text-[#c5cfdd] mt-5 max-w-[46ch] mx-auto">
              {t('landing2.finalCta.subtitle')}
            </p>
            <div className="flex justify-center flex-wrap gap-3.5 mt-8">
              {/* Self-register kapatildi (SELF_REGISTER_AKTIF=false) — misafir icin
                  tek CTA artik dogrudan /login'e gider, "Ucretsiz Basla" metni
                  yaniltici oldugu icin giris metnine cevrildi. Eskiden ayrica
                  bulunan ikinci "Giris Yap" butonu bu yuzden kaldirildi (aynen
                  ayni hedefe giden yinelenen buton olurdu). */}
              <Link
                to={user ? '/dashboard' : '/login'}
                className="inline-flex items-center gap-2 bg-[#C9A227] text-[#241c02] text-base font-extrabold px-8 py-4 rounded-xl hover:brightness-105 hover:-translate-y-0.5 transition-all"
                data-testid="cta-get-started-btn"
              >
                {t(user ? 'landing2.finalCta.ctaPrimary' : 'landing2.finalCta.ctaSecondary')}
                <ArrowRight size={18} aria-hidden="true" />
              </Link>
            </div>

            {stats && (
              <div className="flex justify-center flex-wrap gap-12 mt-14">
                <div>
                  <p className="text-4xl font-black text-[#C9A227]"><AnimatedCounter value={stats.courses} suffix="+" /></p>
                  <p className="text-xs font-bold tracking-widest text-[#7f90a8] mt-1">{localeUpperCase(t('total_courses'), lang)}</p>
                </div>
                <div>
                  <p className="text-4xl font-black text-[#C9A227]"><AnimatedCounter value={stats.students} suffix="+" /></p>
                  <p className="text-xs font-bold tracking-widest text-[#7f90a8] mt-1">{localeUpperCase(t('total_students'), lang)}</p>
                </div>
                <div>
                  <p className="text-4xl font-black text-[#C9A227]"><AnimatedCounter value={stats.lessons} suffix="+" /></p>
                  <p className="text-xs font-bold tracking-widest text-[#7f90a8] mt-1">{localeUpperCase(t('landing2.finalCta.statsLessons'), lang)}</p>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      </section>

      {/* ===== FOOTER ===== */}
      <footer className="bg-[#F7F5F0] text-[#0E1F3D] pt-14 pb-10 px-6" data-testid="duo-footer">
        <div className="max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-10">
          <div>
            <Logo size={30} />
            <p className="text-sm text-[#6b7a70] mt-4 max-w-[30ch] leading-relaxed">
              {t('landing.duo.footer.tagline')}
            </p>
          </div>
          <div>
            <div className="text-xs font-bold tracking-widest text-[#9aa89f] mb-3.5">
              {localeUpperCase(t('landing.duo.footer.product'), lang)}
            </div>
            <div className="flex flex-col gap-2.5">
              <Link to="/courses" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('courses')}</Link>
              <Link to="/programs/kabin-memurlugu" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.langStrip.cabinCrew')}</Link>
              <Link to="/programs/kabin-memurlugu/interview" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing2.footer.interviewSim')}</Link>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold tracking-widest text-[#9aa89f] mb-3.5">
              {localeUpperCase(t('landing.duo.footer.company'), lang)}
            </div>
            <div className="flex flex-col gap-2.5">
              <a href="#" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.footer.about')}</a>
              <a href="#" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.footer.careers')}</a>
              <a
                href={buildWhatsAppUrl(t('whatsapp.message.generic'))}
                target="_blank"
                rel="noopener noreferrer"
                className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]"
                data-testid="footer-contact-whatsapp-link"
              >
                {t('landing.duo.footer.contact')}
              </a>
            </div>
          </div>
          <div>
            <div className="text-xs font-bold tracking-widest text-[#9aa89f] mb-3.5">
              {localeUpperCase(t('landing.duo.footer.support'), lang)}
            </div>
            <div className="flex flex-col gap-2.5">
              <a href="#sss" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.footer.faq')}</a>
              <Link to="/legal/privacy" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.footer.privacy')}</Link>
              <Link to="/legal/terms" className="text-sm font-semibold text-[#5a6b60] hover:text-[#16794a]">{t('landing.duo.footer.terms')}</Link>
            </div>
          </div>
        </div>
        <div className="max-w-6xl mx-auto mt-10 pt-6 border-t border-[#e2ded3] text-center text-[13px] text-[#9aa89f]">
          &copy; 2026 Kariyer Online. {t('landing.duo.footer.rights')}
        </div>
      </footer>
    </div>
  );
}
